/*******************************************************************************
* Copyright (c) Microsoft Corporation.
* All rights reserved. See LICENSE in the project root for license information.
*******************************************************************************/


import 'custom-event-polyfill';

import * as app from '@msdyn365-commerce/runtime-internal/dist/lib/client';
const { getUniqueRequestedAssets } = require('../common');

// Fetches the registration object from a given file and dictionary name
// If the module is not found or dictionary does not exist an empty object is returned
const fetchRegistrationObject = (registration, dictionaryName) => {
    return registration ? registration[dictionaryName] : {};
};

const fetchServerActionsRegistration = async () => {
    try {
        return import('lib/server-actions-registration').catch(e => {
            console.error(`Couldn't load server actions registration.`, e);
        });
    } catch (e) {
        console.error(`Couldn't load server actions registration.`, e);
    }

    return Promise.resolve();
};

const fetchPartnerServerActionsRegistration = async () => {
    try {
        return import('lib/server-actions-registration.local').catch(e => {
            console.error(`Couldn't load partner server actions registration.`, e);
        });
    } catch (e) {
        console.error(`Couldn't load partner server actions registration.`, e);
    }

    return Promise.resolve();
};

// Registers all the local and installed modules need to render the page client-side
const registerModules = async (requestContext, requestedAssets) => {
    let registeredModules = { modules: {}, dataActions: {} };
    let registeredPartnerModules = { modules: {}, dataActions: {} };

    let modulesRegistration;
    let partnerModuleRegistration;

    // installed modules registration
    try {
        modulesRegistration = require('lib/module-registration');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            console.error(`registration file not found.`, e);
        } else {
            throw e;
        }
    }

    // local modules registration
    try {
        partnerModuleRegistration = require('lib/module-registration.local');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`no local modules found.`, e);
        }
    }

    let clientActionsRegistration = { dataActions: {} };
    let partnerClientActionsRegistration = { dataActions: {} };
    let serverActionsRegistration = { dataActions: {} };
    let partnerServerActionsRegistration = { dataActions: {} };
    const disableActionRegOptimization = window.___initialData___._clientPlatformSetting.disableActionRegOptimization || false;

    if (!disableActionRegOptimization) {
        // installed client actions registration
        try {
            clientActionsRegistration = require('lib/client-actions-registration');
        } catch (e) {
            console.error(`Couldn't load client actions registration file.`, e);
        }

        // partner client actions registration
        try {
            partnerClientActionsRegistration = require('lib/client-actions-registration.local');
        } catch (e) {
            console.error(`Couldn't load partner client action registration file.`, e);
        }
    }

    // If in an editor scenario and feature 'enableAuthoringRemoveAndAddModule' has been enabled,
    // register all modules so that editor canvas can add modules client side without page refresh
    if (
        requestContext &&
        requestContext.params &&
        requestContext.params.isEditor &&
        requestContext.features &&
        requestContext.features.enableAuthoringRemoveAndAddModule
    ) {
        if (!disableActionRegOptimization) {
            // installed server actions registration
            serverActionsRegistration = await fetchServerActionsRegistration();

            // partner server actions registration
            partnerServerActionsRegistration = await fetchPartnerServerActionsRegistration();

            serverActionsRegistration =
                serverActionsRegistration && serverActionsRegistration.registerAllActions
                    ? serverActionsRegistration.registerAllActions()
                    : registeredModules;

            partnerServerActionsRegistration =
                partnerServerActionsRegistration && partnerServerActionsRegistration.registerAllActions
                    ? partnerServerActionsRegistration.registerAllActions()
                    : registeredModules;

            clientActionsRegistration =
                clientActionsRegistration && clientActionsRegistration.registerAllActions
                    ? clientActionsRegistration.registerAllActions()
                    : registeredModules;

            partnerClientActionsRegistration =
                partnerClientActionsRegistration && partnerClientActionsRegistration.registerAllActions
                    ? partnerClientActionsRegistration.registerAllActions()
                    : registeredModules;
        }

        registeredModules = modulesRegistration ? modulesRegistration.registerAllModules() : registeredModules;
        registeredPartnerModules = partnerModuleRegistration ? partnerModuleRegistration.registerAllModules() : registeredPartnerModules;
    } else {
        registeredModules = modulesRegistration
            ? modulesRegistration.registerAllModules(requestedAssets && requestedAssets.modules)
            : registeredModules;
        registeredPartnerModules = partnerModuleRegistration
            ? partnerModuleRegistration.registerAllModules(requestedAssets && requestedAssets.modules)
            : registeredPartnerModules;

        if (!disableActionRegOptimization) {
            clientActionsRegistration =
                clientActionsRegistration && clientActionsRegistration.registerAllActions
                    ? clientActionsRegistration.registerAllActions(requestedAssets && requestedAssets.modules)
                    : registeredModules;

            partnerClientActionsRegistration =
                partnerClientActionsRegistration && partnerClientActionsRegistration.registerAllActions
                    ? partnerClientActionsRegistration.registerAllActions(requestedAssets && requestedAssets.modules)
                    : registeredModules;
        }
    }

    return {
        modules: {
            ...registeredModules.modules,
            ...registeredPartnerModules.modules
        },
        dataActions: {
            ...registeredModules.dataActions,
            ...clientActionsRegistration.dataActions,
            ...partnerClientActionsRegistration.dataActions,
            ...serverActionsRegistration.dataActions,
            ...partnerServerActionsRegistration.dataActions
        },
        partnerDataActions: {
            ...registeredPartnerModules.dataActions,
            ...partnerClientActionsRegistration.dataActions,
            ...partnerServerActionsRegistration.dataActions
        }
    };
};

const initializeConnectors = (connectorInformation = {}, connectorsRegistration, requestContext) => {
    let experimentationConnector;
    if (connectorsRegistration && connectorInformation) {
        const expConnectorName = connectorInformation.expConnector;
        if (expConnectorName && connectorsRegistration[expConnectorName]) {
            // Grab the stored config from the window object
            const config = window.___initialData___.experimentationConfig;
            // Grab the experimentation user ID
            const expUserId = requestContext && requestContext.experiments && requestContext.experiments.userId;
            // Initialize the provider and listener for client
            const experimentationProvider = connectorsRegistration[expConnectorName].provider;
            const experimentationListener = connectorsRegistration[expConnectorName].listener;

            if (!experimentationProvider || !experimentationListener) {
                return { experimentation: undefined };
            }

            experimentationProvider.initializeClientSide(config);
            experimentationListener.initializeClientSide(config, expUserId);
            experimentationConnector = { name: expConnectorName, provider: experimentationProvider, listener: experimentationListener };
        }
    }
    return { experimentation: experimentationConnector };
};

const initializePerformanceUtils = async bindings => {
    return import(/* webpackChunkName: "msdyn365-performance-chunk" */ '@msdyn365-commerce/commerce-performance-analyzer').then(lib => {
        bindings.perfAnalysisLibBinding = lib;
    });
};

const initializeClient = async () => {
    const requestedAssets = window.___initialData___ && getUniqueRequestedAssets(window.___initialData___);
    let bindings = { modules: {}, dataActions: {} };
    const requestContext = window.___initialData___ && window.___initialData___.requestContext;

    bindings = await registerModules(requestContext, requestedAssets);

    // view extensions registration
    try {
        bindings.views = fetchRegistrationObject(require('lib/view-registration'), 'viewDictionary');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`view registration files not found.`);
        }
    }

    // component extensions registration
    try {
        bindings.components = fetchRegistrationObject(require('lib/component-registration'), 'componentDictionary');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`component registration files not found.`);
        }
    }

    // connectors registration
    try {
        bindings.connectors = fetchRegistrationObject(require('lib/connector-registration'), 'connectorDictionary');
    } catch (e) {
        if (e.code === 'MODULE_NOT_FOUND') {
            process.env.NODE_ENV === 'development' && console.log(`connector registration files not found.`);
        }
    }

    const connectorInformation = requestContext.connectors;
    const connectors = initializeConnectors(connectorInformation, bindings.connectors, requestContext);

    // eslint-disable-next-line camelcase
    const shouldRunPerformanceAnalysis = requestContext.features?.enable_performance_analysis && requestContext.params.isPerfAnalysis;
    if (shouldRunPerformanceAnalysis) {
        await initializePerformanceUtils(bindings);
    }

    await app.initializeClientApp(bindings, connectors);
    app.clientRender();
};

Promise.resolve().then(async () => {
    await initializeClient();
});
